'use client'

/**
 * Renewal-issue notice + manage/recover CTA for an existing subscription.
 * Stripe portal only on web/Android; native iOS Stripe → manage on web.
 * Never Restore Purchases for Stripe. RC routes by store source.
 * Portal return refreshes via /api/subscription/sync + billing-status (no optimistic Pack Leader).
 *
 * Layout: portaled into `.app-beta-inner-shell` *after* the brand header so it
 * cannot push shellTop (sibling-before-children bug) and cannot cover the logo,
 * main content, or the upgrade modal (z≈110000). Never document-scrolls on open.
 */

import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react'
import { getSession, useSession } from 'next-auth/react'
import { detectNativeIosForUpgrade } from '@/lib/monetization/detectUpgradePlatform'
import { isCapacitorAndroidApp } from '@/lib/auth/capacitorSignIn'
import { restorePurchasesFlow } from '@/lib/monetization/startUpgradeFlow'
import { syncSubscriptionWithServerBestEffort } from '@/lib/monetization/refreshSubscriptionState'
import {
  WAGAMOUR_PLATFORM_HEADER,
  WAGAMOUR_PLATFORM_IOS_NATIVE,
} from '@/lib/monetization/iapConstants'
import type { BillingStatusPayload } from '@/lib/subscription/billingRecovery'
import { WagamourV2Button } from '@/components/v2/WagamourV2Button'
import { homeV2Glass, homeV2Text, homeV2Type } from '@/lib/home/homeV2Theme'
import { parksV2GlassSurface } from '@/lib/parks/parksV2Visual'

export const WAGAMOUR_OPEN_BILLING_RECOVERY_EVENT = 'wagamour:openBillingRecovery'
export const WAGAMOUR_BILLING_STATUS_UPDATED_EVENT = 'wagamour:billingStatusUpdated'

const WAGAMOUR_PLATFORM_ANDROID_NATIVE = 'android-native'
const GOOGLE_PLAY_SUBSCRIPTIONS_URL =
  'https://play.google.com/store/account/subscriptions'
const APPLE_SUBSCRIPTIONS_HELP =
  'https://support.apple.com/en-us/HT202039'
const WEB_MANAGE_URL = 'https://wagamour.com/app-beta?billing=recover'

const BILLING_RECOVERY_SLOT = 'data-billing-recovery-slot'

type NoticeState = BillingStatusPayload | null

function platformHeaderValue(): string | null {
  if (detectNativeIosForUpgrade()) return WAGAMOUR_PLATFORM_IOS_NATIVE
  if (isCapacitorAndroidApp()) return WAGAMOUR_PLATFORM_ANDROID_NATIVE
  return null
}

async function fetchBillingStatus(): Promise<BillingStatusPayload | null> {
  const headers: HeadersInit = { Accept: 'application/json' }
  const platform = platformHeaderValue()
  if (platform) headers[WAGAMOUR_PLATFORM_HEADER] = platform
  const res = await fetch('/api/subscription/billing-status', {
    method: 'GET',
    credentials: 'include',
    headers,
    cache: 'no-store',
  })
  if (!res.ok) return null
  return (await res.json()) as BillingStatusPayload
}

/** DB/entitlement sync only — never applyPackLeaderTierLocally. */
async function refreshBillingViaResolver(options?: {
  /** Pull Stripe → DB before sync (portal return / resume). */
  refreshFromStripe?: boolean
}): Promise<BillingStatusPayload | null> {
  try {
    await fetch('/api/subscription/sync', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        refreshFromStripe: Boolean(options?.refreshFromStripe),
      }),
      cache: 'no-store',
    })
  } catch {
    /* non-fatal */
  }
  try {
    await getSession()
  } catch {
    /* non-fatal */
  }
  return fetchBillingStatus()
}

async function openExternalUrl(url: string): Promise<void> {
  try {
    const cap = (
      window as unknown as { Capacitor?: { Plugins?: Record<string, any> } }
    ).Capacitor
    const Browser = cap?.Plugins?.Browser
    if (Browser && typeof Browser.open === 'function') {
      await Browser.open({ url })
      return
    }
  } catch {
    /* fall through */
  }
  window.location.assign(url)
}

/** Ensure an in-shell host after the brand header (never a pre-shell sibling). */
function ensureBillingRecoverySlot(): HTMLElement | null {
  if (typeof document === 'undefined') return null
  const shell = document.querySelector<HTMLElement>('.app-beta-inner-shell')
  if (!shell) return null

  let host = shell.querySelector<HTMLElement>(`[${BILLING_RECOVERY_SLOT}="true"]`)
  if (host) return host

  host = document.createElement('div')
  host.setAttribute(BILLING_RECOVERY_SLOT, 'true')
  host.className = 'relative z-[96] w-full flex-shrink-0'
  host.setAttribute('data-billing-recovery-overlay', 'in-shell')

  const header = shell.querySelector<HTMLElement>(
    '[data-home-v2-brand-header="true"]'
  )
  if (header) {
    header.insertAdjacentElement('afterend', host)
    return host
  }
  const sticky = shell.querySelector<HTMLElement>(':scope > .sticky')
  if (sticky) {
    sticky.insertAdjacentElement('afterend', host)
    return host
  }
  shell.insertBefore(host, shell.firstChild)
  return host
}

export function BillingRecoveryNotice() {
  const { status: sessionStatus } = useSession()
  const [status, setStatus] = useState<NoticeState>(null)
  const [forceShow, setForceShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [slotEl, setSlotEl] = useState<HTMLElement | null>(null)

  const applyStatus = useCallback((next: BillingStatusPayload | null) => {
    setStatus(next)
    if (typeof window !== 'undefined' && next) {
      window.dispatchEvent(
        new CustomEvent(WAGAMOUR_BILLING_STATUS_UPDATED_EVENT, { detail: next })
      )
    }
  }, [])

  const refresh = useCallback(async () => {
    if (sessionStatus !== 'authenticated') {
      setStatus(null)
      return
    }
    applyStatus(await fetchBillingStatus())
  }, [sessionStatus, applyStatus])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // Portal / web return: Stripe pull + sync + billing-status; poll while webhook lags.
  useEffect(() => {
    if (sessionStatus !== 'authenticated') return
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const billing = params.get('billing')
    if (billing !== 'portal_return' && billing !== 'recover') return

    let cancelled = false
    let pollTimer: number | null = null
    ;(async () => {
      setBusy(true)
      setMessage(null)
      setForceShow(true)
      try {
        const next = await refreshBillingViaResolver({ refreshFromStripe: true })
        if (cancelled) return
        applyStatus(next)
        if (next?.needsRecovery) {
          setMessage(
            'Still showing a renewal issue. Updating a payment method alone is not enough — wait for Stripe to confirm payment, or tap Refresh status.'
          )
          // Delayed webhook: poll Stripe→DB for ~30s without signing the user out.
          let attempts = 0
          const poll = async () => {
            if (cancelled) return
            attempts += 1
            const again = await refreshBillingViaResolver({
              refreshFromStripe: true,
            })
            if (cancelled) return
            applyStatus(again)
            if (!again?.needsRecovery) {
              setForceShow(false)
              setMessage(
                'Payment confirmed — Pack Leader access restored from your account.'
              )
              return
            }
            if (attempts < 10) {
              pollTimer = window.setTimeout(() => {
                void poll()
              }, 3000)
            } else {
              setMessage(
                'Still waiting on Stripe confirmation. Tap Refresh status after a moment — no need to sign out.'
              )
            }
          }
          pollTimer = window.setTimeout(() => {
            void poll()
          }, 3000)
        } else {
          setForceShow(false)
          setMessage(
            'Payment confirmed — Pack Leader access restored from your account.'
          )
        }
        // Clean query without reload
        params.delete('billing')
        const url = `${window.location.pathname}${params.toString() ? `?${params}` : ''}${window.location.hash || ''}`
        window.history.replaceState({}, '', url)
      } finally {
        if (!cancelled) setBusy(false)
      }
    })()
    return () => {
      cancelled = true
      if (pollTimer != null) window.clearTimeout(pollTimer)
    }
  }, [sessionStatus, applyStatus])

  // App resume / foreground while recovery is active: re-pull Stripe (webhook lag).
  useEffect(() => {
    if (sessionStatus !== 'authenticated') return
    if (!status?.needsRecovery && !forceShow) return
    const onResume = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') {
        return
      }
      void (async () => {
        const next = await refreshBillingViaResolver({ refreshFromStripe: true })
        applyStatus(next)
        if (next && !next.needsRecovery) {
          setForceShow(false)
          setMessage(
            'Payment confirmed — Pack Leader access restored from your account.'
          )
        }
      })()
    }
    document.addEventListener('visibilitychange', onResume)
    window.addEventListener('pageshow', onResume)
    return () => {
      document.removeEventListener('visibilitychange', onResume)
      window.removeEventListener('pageshow', onResume)
    }
  }, [sessionStatus, applyStatus, status?.needsRecovery, forceShow])

  useEffect(() => {
    const onOpen = () => {
      setForceShow(true)
      void refresh()
      // In-shell slot (below) — do not document-scroll on open. Smooth scrolling
      // from a deep Picks CTA moved scrollY before any modal opened (v4 desktop
      // dump: tapY≈1893 → Δ-1128 by 600ms with modalOpen=false).
    }
    window.addEventListener(WAGAMOUR_OPEN_BILLING_RECOVERY_EVENT, onOpen)
    return () => {
      window.removeEventListener(WAGAMOUR_OPEN_BILLING_RECOVERY_EVENT, onOpen)
    }
  }, [refresh])

  const visible =
    Boolean(status?.needsRecovery) ||
    (forceShow && Boolean(status?.needsRecovery))

  useEffect(() => {
    if (!visible) {
      setSlotEl(null)
      return
    }
    const place = () => setSlotEl(ensureBillingRecoverySlot())
    place()
    const id = window.setInterval(place, 1000)
    return () => {
      window.clearInterval(id)
    }
  }, [visible])

  if (!visible || !status?.notice) return null

  const handlePrimary = async () => {
    setMessage(null)
    setBusy(true)
    try {
      const action = status.platformAction

      if (action === 'stripe_manage_on_web') {
        await openExternalUrl(WEB_MANAGE_URL)
        return
      }

      if (action === 'stripe_customer_portal') {
        if (detectNativeIosForUpgrade()) {
          setMessage(
            'Stripe billing can’t be managed in the iOS app. Open Wagamour in Safari to update payment.'
          )
          return
        }
        const res = await fetch('/api/subscription/portal', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(platformHeaderValue()
              ? { [WAGAMOUR_PLATFORM_HEADER]: platformHeaderValue()! }
              : {}),
          },
          body: JSON.stringify({
            returnUrl:
              typeof window !== 'undefined'
                ? `${window.location.origin}/app-beta`
                : 'https://wagamour.com/app-beta',
          }),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) {
          setMessage(
            data?.message || data?.error || 'Unable to open billing portal.'
          )
          return
        }
        if (data?.url) {
          await openExternalUrl(String(data.url))
          return
        }
        setMessage('Billing portal URL missing.')
        return
      }

      if (action === 'apple_restore_purchases') {
        if (status.provider === 'stripe') {
          setMessage(
            'This subscription is billed with Stripe. Restore Purchases will not fix it — open Wagamour on the web to update payment.'
          )
          return
        }
        const result = await restorePurchasesFlow()
        if (!result.success) {
          setMessage(result.error)
          return
        }
        await syncSubscriptionWithServerBestEffort()
        applyStatus(await fetchBillingStatus())
        setMessage(
          result.restored
            ? 'Purchase restore requested. Access updates after Apple/RevenueCat verification syncs.'
            : 'No active Apple subscription found. Manage billing in Apple ID → Subscriptions, then tap Refresh status.'
        )
        return
      }

      if (action === 'apple_subscription_settings') {
        await openExternalUrl(APPLE_SUBSCRIPTIONS_HELP)
        return
      }

      if (action === 'google_play_subscriptions') {
        await openExternalUrl(GOOGLE_PLAY_SUBSCRIPTIONS_URL)
        return
      }

      setMessage(
        'Manage billing in the store where you originally subscribed, then tap Refresh status.'
      )
    } finally {
      setBusy(false)
    }
  }

  const handleRefresh = async () => {
    setBusy(true)
    setMessage(null)
    try {
      if (status.provider === 'revenuecat') {
        await syncSubscriptionWithServerBestEffort()
      }
      const next = await refreshBillingViaResolver({
        refreshFromStripe: status.provider === 'stripe',
      })
      applyStatus(next)
      if (next?.needsRecovery) {
        setMessage(
          'Status refreshed — renewal issue still present until the provider confirms payment. Updating a card alone does not restore Pack Leader.'
        )
      } else {
        setForceShow(false)
        setMessage(
          'Payment confirmed — Pack Leader access restored from your account.'
        )
      }
    } finally {
      setBusy(false)
    }
  }

  // Night / photo backgrounds: use V2 dark glass + light type (amber-950 vanishes on cityscape).
  const notice = (
    <div
      role="status"
      data-testid="billing-recovery-notice"
      data-billing-provider={status.provider}
      data-billing-status={status.status ?? ''}
      data-billing-platform={status.platform ?? ''}
      data-billing-action={status.platformAction}
      data-billing-store={status.storeSource ?? ''}
      data-billing-can-checkout={String(status.canStartNewCheckout)}
      data-billing-notice-theme="v2-dark-glass"
      className="mx-3 mt-2 mb-1 rounded-2xl px-4 py-3 text-left"
      style={{
        ...parksV2GlassSurface,
        background:
          'linear-gradient(165deg, rgba(22, 18, 20, 0.88) 0%, rgba(32, 26, 28, 0.78) 55%, rgba(40, 34, 36, 0.72) 100%)',
        border: `1px solid ${homeV2Glass.surface.border}`,
        boxShadow:
          '0 10px 28px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 236, 210, 0.14)',
      }}
    >
      <div className="flex gap-3">
        <AlertCircle
          className="mt-0.5 h-5 w-5 shrink-0 text-amber-200"
          aria-hidden
        />
        <div className="min-w-0 flex-1 space-y-2">
          <p
            className="text-sm font-semibold"
            style={homeV2Type(homeV2Text.headline)}
          >
            {status.notice.title}
          </p>
          <p
            className="text-sm leading-snug"
            style={homeV2Type(homeV2Text.body)}
          >
            {status.notice.body}
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <WagamourV2Button
              type="button"
              size="sm"
              disabled={busy}
              onClick={() => void handlePrimary()}
              data-testid="billing-recovery-primary"
            >
              {busy ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : null}
              {status.notice.primaryCtaLabel}
            </WagamourV2Button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleRefresh()}
              className="inline-flex items-center gap-1 text-xs font-medium underline-offset-2 hover:underline disabled:opacity-50"
              style={homeV2Type(homeV2Text.warm)}
              data-testid="billing-recovery-refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh status
            </button>
          </div>
          {message ? (
            <p
              className="text-xs"
              style={homeV2Type(homeV2Text.metadata)}
              data-testid="billing-recovery-message"
            >
              {message}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )

  // Prefer in-shell portal (below logo). Fall back to null until shell mounts —
  // never render as a pre-shell in-flow sibling (shellTop≈141 bug).
  if (slotEl) return createPortal(notice, slotEl)
  return null
}

export async function shouldOpenBillingRecoveryInsteadOfUpgrade(): Promise<boolean> {
  try {
    const status = await fetchBillingStatus()
    if (status?.needsRecovery) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(WAGAMOUR_OPEN_BILLING_RECOVERY_EVENT))
      }
      return true
    }
  } catch {
    /* fall through */
  }
  return false
}
